import React from 'react';

// ── Color tables ──────────────────────────────────────────────────────────────

const LEGACY_COLORS = {
  '0': 'black',      '1': 'dark_blue',    '2': 'dark_green',  '3': 'dark_aqua',
  '4': 'dark_red',   '5': 'dark_purple',  '6': 'gold',        '7': 'gray',
  '8': 'dark_gray',  '9': 'blue',         'a': 'green',       'b': 'aqua',
  'c': 'red',        'd': 'light_purple', 'e': 'yellow',      'f': 'white',
};

const MM_COLORS = {
  black: '#000000',       dark_blue: '#0000AA',    dark_green: '#00AA00',
  dark_aqua: '#00AAAA',   dark_red: '#AA0000',     dark_purple: '#AA00AA',
  gold: '#FFAA00',        gray: '#AAAAAA',          dark_gray: '#555555',
  blue: '#5555FF',        green: '#55FF55',         aqua: '#55FFFF',
  red: '#FF5555',         light_purple: '#FF55FF',  yellow: '#FFFF55',
  white: '#FFFFFF',
  grey: '#AAAAAA',
};

const FORMAT_TAGS = new Set(['bold', 'italic', 'underlined', 'strikethrough', 'obfuscated']);

// ── Color utilities ───────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b]
    .map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0'))
    .join('');
}

function resolveColor(str) {
  const s = str.trim().toLowerCase();
  if (MM_COLORS[s]) return MM_COLORS[s];
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  return null;
}

function gradientColor(stops, t) {
  if (stops.length === 1) return stops[0];
  const scaled = t * (stops.length - 1);
  const i = Math.min(Math.floor(scaled), stops.length - 2);
  const local = scaled - i;
  const [ar, ag, ab] = hexToRgb(stops[i]);
  const [br, bg, bb] = hexToRgb(stops[i + 1]);
  return rgbToHex(ar + (br - ar) * local, ag + (bg - ag) * local, ab + (bb - ab) * local);
}

function rainbowColor(ci, total, phase = 0) {
  const hue = ((ci / Math.max(total - 1, 1)) + phase) % 1 * 360;
  const h = hue / 60;
  const x = 1 - Math.abs(h % 2 - 1);
  let r = 0, g = 0, b = 0;
  if      (h < 1) { r = 1; g = x; }
  else if (h < 2) { r = x; g = 1; }
  else if (h < 3) {         g = 1; b = x; }
  else if (h < 4) {         g = x; b = 1; }
  else if (h < 5) { r = x;        b = 1; }
  else            { r = 1;        b = x; }
  return rgbToHex(r * 255, g * 255, b * 255);
}

// ── Scopes & Sizing Helpers ──────────────────────────────────────────────────

function getPlainTextLength(text, from, to) {
  let count = 0;
  for (let i = from; i < to; i++) {
    if (text[i] === '<') {
      const end = text.indexOf('>', i);
      if (end !== -1 && end < to) {
        if (!text.substring(i + 1, end).includes('<')) {
          i = end;
          continue;
        }
      }
    }
    count++;
  }
  return count;
}

function findMatchingCloseTag(text, tagName, from) {
  const openPrefix = `<${tagName}`;
  const closeStr   = `</${tagName}>`;
  let depth = 1, i = from;
  while (i < text.length) {
    if (text.startsWith(closeStr, i)) {
      if (--depth === 0) return i;
      i += closeStr.length;
    } else if (
      text.startsWith(openPrefix, i) &&
      (text[i + openPrefix.length] === '>' || text[i + openPrefix.length] === ':')
    ) {
      depth++;
      i += openPrefix.length;
    } else {
      i++;
    }
  }
  return -1;
}

// ── Pass 1: legacy → MiniMessage ─────────────────────────────────────────────

function convertLegacyToMiniMessage(text) {
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if ((ch === '&' || ch === '§') && i + 1 < text.length) {
      const next = text[i + 1];

      if (next === '#' && i + 7 < text.length) {
        const hex = text.substring(i + 2, i + 8);
        if (/^[0-9a-fA-F]{6}$/.test(hex)) {
          out += `<#${hex.toLowerCase()}>`;
          i += 7;
          continue;
        }
      }

      if (next.toLowerCase() === 'x' && i + 13 < text.length) {
        let hex = '', valid = true;
        for (let j = 0; j < 6; j++) {
          if (text[i + 2 + j * 2] !== '&' && text[i + 2 + j * 2] !== '§') { valid = false; break; }
          hex += text[i + 3 + j * 2];
        }
        if (valid && /^[0-9a-fA-F]{6}$/.test(hex)) {
          out += `<#${hex.toLowerCase()}>`;
          i += 13;
          continue;
        }
      }

      const code = next.toLowerCase();
      if (LEGACY_COLORS[code])    { out += `<${LEGACY_COLORS[code]}>`; i++; continue; }
      if ('lnomk'.includes(code)) { i++; continue; } 
      if (code === 'r')           { out += '<reset>'; i++; continue; }
    }
    out += ch;
  }
  return out;
}

// ── Pass 2: MiniMessage → React elements ─────────────────────────────────────

function parseMiniMessage(text) {
  const elements = [];
  let styleStack = []; 
  let buf   = '';
  let key   = 0;
  let lastColor = undefined;

  const flush = () => {
    if (!buf) return;
    elements.push(
      <span key={key++} style={lastColor !== undefined ? { color: lastColor } : {}}>{buf}</span>
    );
    buf = '';
  };

  // Helper to process printable characters consistently across literal branches
  const processChar = (ch) => {
    let resolvedColor = undefined;
    
    // Scan stack from top-to-bottom to get active style color
    for (let j = styleStack.length - 1; j >= 0; j--) {
      const entry = styleStack[j];
      if (entry.type === 'color') {
        resolvedColor = entry.color;
        break;
      } else if (entry.type === 'gradient') {
        let t = entry.totalLen <= 1 ? 0 : entry.currentCount / (entry.totalLen - 1);
        if (entry.phase !== 0) t = (t + entry.phase) % 1;
        resolvedColor = gradientColor(entry.stops, t);
        break;
      } else if (entry.type === 'rainbow') {
        resolvedColor = rainbowColor(entry.currentCount, entry.totalLen, entry.phase);
        break;
      }
    }

    if (resolvedColor !== lastColor) {
      flush();
      lastColor = resolvedColor;
    }

    buf += ch;

    // Tick down step indexes for ALL dynamic style layers currently active
    for (let j = 0; j < styleStack.length; j++) {
      if (styleStack[j].type === 'gradient' || styleStack[j].type === 'rainbow') {
        styleStack[j].currentCount++;
      }
    }
  };

  for (let i = 0; i < text.length; i++) {
    if (text[i] !== '<') {
      processChar(text[i]);
      continue;
    }

    const end = text.indexOf('>', i);
    // Lone '<', process literally
    if (end === -1) {
      processChar(text[i]);
      continue;
    }

    // Mathematical/Incomplete '<' sequence, process literally
    if (text.substring(i + 1, end).includes('<')) {
      processChar(text[i]);
      continue;
    }

    // Valid structural tag encountered — clear buffer before state modification
    flush();

    const raw     = text.substring(i + 1, end).toLowerCase().trim();
    const closing = raw.startsWith('/');
    const tag     = closing ? raw.slice(1).trim() : raw;

    // ── <gradient:c1:c2[:c3...[:phase]]> ────────────────────────────────────
    if (!closing && tag.startsWith('gradient:')) {
      const parts = tag.slice('gradient:'.length).split(':');
      let colorParts = parts, phase = 0;

      const last = parts[parts.length - 1];
      if (parts.length > 2 && !resolveColor(last) && !isNaN(parseFloat(last))) {
        phase = ((parseFloat(last) % 1) + 1) % 1;  
        colorParts = parts.slice(0, -1);
      }

      const stops = colorParts.map(resolveColor).filter(Boolean);
      if (stops.length >= 2) {
        const regionStart = end + 1;
        let closingAt = findMatchingCloseTag(text, 'gradient', regionStart);
        if (closingAt === -1) closingAt = text.length;

        const totalLen = getPlainTextLength(text, regionStart, closingAt);
        styleStack.push({
          type: 'gradient',
          stops,
          phase,
          totalLen,
          currentCount: 0
        });
        i = end;
        continue;
      }
      buf += text.substring(i, end + 1);
      i = end;
      continue;
    }

    // ── </gradient> ────────────────────────────────────────────────────────
    if (closing && tag === 'gradient') {
      for (let j = styleStack.length - 1; j >= 0; j--) {
        if (styleStack[j].type === 'gradient') {
          styleStack.splice(j, 1);
          break;
        }
      }
      i = end;
      continue;
    }

    // ── <rainbow[:phase]> ───────────────────────────────────────────────────
    if (!closing && (tag === 'rainbow' || tag.startsWith('rainbow:'))) {
      const phase = tag.includes(':')
        ? ((parseFloat(tag.split(':')[1]) || 0) % 1 + 1) % 1
        : 0;
      const regionStart = end + 1;
      let closingAt = findMatchingCloseTag(text, 'rainbow', regionStart);
      if (closingAt === -1) closingAt = text.length;

      const totalLen = getPlainTextLength(text, regionStart, closingAt);
      styleStack.push({
        type: 'rainbow',
        phase,
        totalLen,
        currentCount: 0
      });
      i = end;
      continue;
    }

    // ── </rainbow> ─────────────────────────────────────────────────────────
    if (closing && tag === 'rainbow') {
      for (let j = styleStack.length - 1; j >= 0; j--) {
        if (styleStack[j].type === 'rainbow') {
          styleStack.splice(j, 1);
          break;
        }
      }
      i = end;
      continue;
    }

    // ── <color:#rrggbb> / <colour:#rrggbb> / <c:#rrggbb> ────────────────────
    if (!closing && (tag.startsWith('color:') || tag.startsWith('colour:') || tag.startsWith('c:'))) {
      const resolved = resolveColor(tag.slice(tag.indexOf(':') + 1));
      if (resolved) { styleStack.push({ type: 'color', color: resolved }); i = end; continue; }
    }

    // ── </color> / </colour> / </c> ─────────────────────────────────────────
    if (closing && (tag === 'color' || tag === 'colour' || tag === 'c')) {
      for (let j = styleStack.length - 1; j >= 0; j--) {
        if (styleStack[j].type === 'color') {
          styleStack.splice(j, 1);
          break;
        }
      }
      i = end;
      continue;
    }

    // ── Named color (<red>, <dark_blue>, <gold>, …) ──────────────────────────
    if (MM_COLORS[tag]) {
      if (closing) {
        const targetColor = MM_COLORS[tag];
        for (let j = styleStack.length - 1; j >= 0; j--) {
          if (styleStack[j].type === 'color' && styleStack[j].color === targetColor) {
            styleStack.splice(j, 1);
            break;
          }
        }
      } else {
        styleStack.push({ type: 'color', color: MM_COLORS[tag] });
      }
      i = end;
      continue;
    }

    // ── Inline hex: <#rrggbb> ────────────────────────────────────────────────
    if (/^#[0-9a-f]{6}$/.test(tag)) {
      if (closing) {
        for (let j = styleStack.length - 1; j >= 0; j--) {
          if (styleStack[j].type === 'color' && styleStack[j].color === tag) {
            styleStack.splice(j, 1);
            break;
          }
        }
      } else {
        styleStack.push({ type: 'color', color: tag });
      }
      i = end;
      continue;
    }

    // ── <reset> ──────────────────────────────────────────────────────────────
    if (tag === 'reset') {
      styleStack = []; i = end; continue;
    }

    // ── Format tags: consume silently, apply nothing ─────────────────────────
    if (FORMAT_TAGS.has(tag)) { i = end; continue; }

    // ── Unknown tag: pass through literally so it appears in output ──────────
    buf += text.substring(i, end + 1);
    i = end;
  }

  flush();
  return elements;
}

function ColorParse(text) {
  if (!text) return null;
  return <>{parseMiniMessage(convertLegacyToMiniMessage(text))}</>;
}

export function getPlainText(text) {
  if (!text) return '';
  const mm = convertLegacyToMiniMessage(String(text));
  return mm.replace(/<[^>]+>/g, '').trim();
}

export default ColorParse;